import type { ReactNode } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useConditionLiveSummary } from '@/components/audiences/use-condition-live-summary';
import { TreeDndBox } from '@/components/tree-dnd/tree-dnd-box';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { TreeDndNotLabel } from '@/components/tree-dnd/tree-dnd-not-label';
import { TreeDndOperatorLabel } from '@/components/tree-dnd/tree-dnd-operator-label';
import type {
  GroupOperator,
  TreeConditionNode,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import { cn } from '@/lib/utils';

function branchPosition(
  index: number,
  count: number
): 'first' | 'middle' | 'last' {
  if (index === 0) return 'first';
  if (index === count - 1) return 'last';
  return 'middle';
}

function OperatorRail({
  operator,
  children,
}: {
  operator: GroupOperator;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center">
      <div className="flex shrink-0 items-center">
        <TreeDndOperatorLabel operator={operator} />
        <div className="h-px w-2 bg-border" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function OperatorRailBranch({
  position,
  children,
}: {
  position: 'first' | 'middle' | 'last';
  children: ReactNode;
}) {
  return (
    <div className="flex items-stretch">
      <div className="relative w-3 shrink-0">
        <div
          className={cn(
            'absolute left-0 w-px bg-border',
            position === 'first' && 'bottom-0 top-1/2',
            position === 'middle' && 'inset-y-0',
            position === 'last' && 'top-0 bottom-1/2'
          )}
          aria-hidden
        />
        <div
          className="absolute top-1/2 left-0 h-px w-full bg-border"
          aria-hidden
        />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function OperatorRailSpine({ className }: { className?: string }) {
  return (
    <div className={cn('relative w-3 shrink-0', className)} aria-hidden>
      <div className="absolute inset-y-0 left-0 w-px bg-border" />
    </div>
  );
}

function NotRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="flex shrink-0 items-center pt-1">
        <TreeDndNotLabel />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function SiblingGap() {
  return <div className="min-h-4 py-1" aria-hidden />;
}

function ReadOnlyConditionRow({ node }: { node: TreeConditionNode }) {
  const { LL } = useI18nContext();
  const summaryDetail = useConditionLiveSummary(node);
  const conditionLabel = LL.audiences.filters.conditions[node.kind]();

  return (
    <TreeDndBox
      nodeType="condition"
      showHandle={false}
      className="border-0 bg-transparent"
      headerExtra={
        <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      }
    />
  );
}

function GroupChildren({
  operator,
  children,
}: {
  operator: GroupOperator;
  children: TreeNode[];
}) {
  const showOperatorRail = children.length > 1;

  const childList = children.map((child, index) => {
    const childNode = <AudienceFilterTreeReadOnlyNode node={child} />;

    return (
      <div key={child.id}>
        {showOperatorRail ? (
          <OperatorRailBranch position={branchPosition(index, children.length)}>
            {childNode}
          </OperatorRailBranch>
        ) : (
          childNode
        )}
        {index < children.length - 1 ? (
          showOperatorRail ? (
            <div className="flex items-stretch">
              <OperatorRailSpine className="self-stretch" />
              <div className="min-w-0 flex-1">
                <SiblingGap />
              </div>
            </div>
          ) : (
            <SiblingGap />
          )
        ) : null}
      </div>
    );
  });

  if (showOperatorRail) {
    return <OperatorRail operator={operator}>{childList}</OperatorRail>;
  }

  return <div className="flex flex-col">{childList}</div>;
}

function AudienceFilterTreeReadOnlyNode({ node }: { node: TreeNode }) {
  if (node.negated) {
    return (
      <NotRow>
        <AudienceFilterTreeReadOnlyNode node={{ ...node, negated: false }} />
      </NotRow>
    );
  }

  if (isGroupNode(node)) {
    return <GroupChildren operator={node.operator} children={node.children} />;
  }

  return <ReadOnlyConditionRow node={node} />;
}

export function AudienceFilterTreeReadOnly({
  root,
  className,
}: {
  root: TreeGroupNode | null;
  className?: string;
}) {
  const { LL } = useI18nContext();

  if (!root || root.children.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {LL.audiences.details.overview.filterConditions.empty()}
      </p>
    );
  }

  return (
    <div className={cn('-mx-1 overflow-x-auto px-1', className)}>
      <div className="min-w-max">
        <AudienceFilterTreeReadOnlyNode node={root} />
      </div>
    </div>
  );
}
